import { useEffect, useRef, useState, useCallback } from 'react';
import { ZIBUZZ_WEBSOCKET } from '../config/mappedEnv';

const HEARTBEAT_MS = 4.5 * 60 * 1000;
const ACTIVE_WINDOW_MS = 5 * 60 * 1000;
const MAX_RECONNECT_DELAY_MS = 30000;

/**
 * useSocket Hook
 * Establishes a WebSocket connection and provides real-time messages.
 * */
export function useSocket(userId, phoneId, enabled = true) {
  const socketRef = useRef(null);
  const connectRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const manualCloseRef = useRef(false);
  const lastActivityRef = useRef(Date.now());
  const [lastMessage, setLastMessage] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const isUserActive = useCallback(() => {
    const visible =
      typeof document === 'undefined' || document.visibilityState === 'visible';
    const online = typeof navigator === 'undefined' || navigator.onLine !== false;
    const recentlyActive = Date.now() - lastActivityRef.current < ACTIVE_WINDOW_MS;

    return visible && online && recentlyActive;
  }, []);

  const scheduleReconnect = useCallback(() => {
    if (!userId || !phoneId || !enabled || !isUserActive()) return;
    if (reconnectTimerRef.current) return;

    const attempt = reconnectAttemptRef.current;
    const baseDelay = Math.min(1000 * (2 ** attempt), MAX_RECONNECT_DELAY_MS);
    const jitter = Math.floor(Math.random() * 500);
    const delay = baseDelay + jitter;

    reconnectAttemptRef.current += 1;
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      connectRef.current?.();
    }, delay);
  }, [enabled, isUserActive, phoneId, userId]);

  const connect = useCallback(() => {
    if (!userId || !phoneId || !enabled) return;
    if (!isUserActive()) return;

    clearReconnectTimer();

    // Close existing connection if any
    if (
      socketRef.current &&
      socketRef.current.readyState !== WebSocket.CLOSING &&
      socketRef.current.readyState !== WebSocket.CLOSED
    ) {
      manualCloseRef.current = true;
      socketRef.current.close();
    }

    // Convert https to wss if necessary
    let wsUrl = ZIBUZZ_WEBSOCKET;
    if (typeof wsUrl !== 'string' || !wsUrl.trim()) {
      setError(new Error('ZIBUZZ_WEBSOCKET is not configured'));
      return;
    }
    if (wsUrl.startsWith('http')) {
      wsUrl = wsUrl.replace('http', 'ws');
    }

    // Construct URL with query parameters as expected by the backend
    try {
      const url = new URL(wsUrl);
      url.searchParams.append('userId', userId);
      url.searchParams.append('phoneId', phoneId);

      manualCloseRef.current = false;
      const socket = new WebSocket(url.toString());
      socketRef.current = socket;

      socket.onopen = () => {
        console.log('[WebSocket] Connected');
        reconnectAttemptRef.current = 0;
        setIsConnected(true);
        setError(null);

        // Start heartbeat (ping) every 3 minutes to prevent 10-min idle timeout
        const pingInterval = setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ action: 'ping' }));
          }
        }, HEARTBEAT_MS);

        socket._pingInterval = pingInterval;
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          // Ignore server-side pong/acknowledgments if any
          if (data.type === 'pong') return;

          setLastMessage({
            ...data,
            _receivedAt: new Date().toISOString()
          });
        } catch (err) {
          console.error('[WebSocket] Error parsing message:', err);
        }
      };

      socket.onclose = (event) => {
        console.log('[WebSocket] Disconnected', event.code, event.reason);
        setIsConnected(false);
        if (socket._pingInterval) clearInterval(socket._pingInterval);
        const isCurrentSocket = socketRef.current === socket;
        if (isCurrentSocket) {
          socketRef.current = null;
        }

        if (isCurrentSocket && !manualCloseRef.current && enabled && isUserActive()) {
          scheduleReconnect();
        }
      };

      socket.onerror = (err) => {
        console.error('[WebSocket] Error:', err);
        setError(err);
        if (socket._pingInterval) clearInterval(socket._pingInterval);
      };
    } catch (err) {
      console.error('[WebSocket] URL Construction Error:', err);
      setError(err);
    }
  }, [userId, phoneId, enabled, clearReconnectTimer, isUserActive, scheduleReconnect]);

  connectRef.current = connect;

  useEffect(() => {
    connect();
    return () => {
      clearReconnectTimer();
      manualCloseRef.current = true;
      if (socketRef.current) {
        socketRef.current.close();
      }
    };
  }, [clearReconnectTimer, connect]);

  useEffect(() => {
    const markActive = () => {
      lastActivityRef.current = Date.now();
      if (
        enabled &&
        (!socketRef.current || socketRef.current.readyState === WebSocket.CLOSED)
      ) {
        reconnectAttemptRef.current = 0;
        connect();
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        markActive();
      }
    };

    window.addEventListener('focus', markActive);
    window.addEventListener('online', markActive);
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('mousemove', markActive, { passive: true });
    window.addEventListener('keydown', markActive);
    window.addEventListener('touchstart', markActive, { passive: true });

    return () => {
      window.removeEventListener('focus', markActive);
      window.removeEventListener('online', markActive);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('mousemove', markActive);
      window.removeEventListener('keydown', markActive);
      window.removeEventListener('touchstart', markActive);
    };
  }, [connect, enabled]);

  return { lastMessage, isConnected, error };
}
